import React, {ReactNode} from 'react';

import makeCustomGroupButtonActionFunction from 'notebook/notes/groups/action-functions/makeCustomGroupButtonActionFunction';
import {
  GroupActionFunctionParameters,
  OverflowMenuGroupActionFunction,
  ToolbarButtonGroupActionFunction,
} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';

export type GroupActionParamCallback<TTableMetadata, TRowMetadata, T> = (
  params: GroupActionFunctionParameters<TTableMetadata, TRowMetadata>
) => T;

interface MakeSimpleGroupActionFunctionParams<TGroupMetadata, TCreateGroupExtras> {
  // A short name for the action, suitable for display in a button or menu item.
  label: string | GroupActionParamCallback<TGroupMetadata, TCreateGroupExtras, string>;

  // Displayed on the button or menu item.
  icon:
    | BenchlingIconName
    | JSX.Element
    | GroupActionParamCallback<TGroupMetadata, TCreateGroupExtras, BenchlingIconName | JSX.Element>;
  iconAltText?: string;

  // Called when the user clicks the button or menu item.
  onInvoke: (params: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>) => void;

  // The design parameters are ignored when an action function is rendered as an overflow menu item
  buttonDesign?: {
    // Defaults to ButtonStyle.TERTIARY_GRAY
    buttonStyle?: ButtonStyle | GroupActionParamCallback<TGroupMetadata, TCreateGroupExtras, ButtonStyle>;

    // Defaults to false
    showTextOnButton?: boolean;
  };

  // Return the tooltip text, or null to hide the tooltip. If undefined, the tooltip defaults to the action
  // function label (for buttons) or no tooltip (for overflow menu items). This should never return just the
  // action name because such a tooltip would be redundant for menu items.
  getTooltip?: (params: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>) => string | null;

  // Return whether the button should be disabled. If not provided, the button will always be enabled. If the
  // action is not permitted in locked entries, this should return true if params.locked is true.
  getDisabled?: (params: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>) => boolean;

  // Return whether the button should be hidden completely.
  getHidden?: (params: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>) => boolean;
}

/**
 * Returns param if it is not a function, otherwise evaluates param using actionFunctionParams.
 */
function evaluateFunctionalParam<T, TTableMetadata, TRowMetadata>(
  param: Exclude<T, Function> | GroupActionParamCallback<TTableMetadata, TRowMetadata, T>,
  actionFunctionParams: GroupActionFunctionParameters<TTableMetadata, TRowMetadata>
): T {
  return typeof param === 'function'
    ? (param as GroupActionParamCallback<TTableMetadata, TRowMetadata, T>)(actionFunctionParams)
    : param;
}

/**
 * Create an ActionFunction that can be placed in either the toolbar or the overflow menu.
 */
export default function makeSimpleGroupActionFunction<TGroupMetadata, TCreateGroupExtras>({
  label,
  icon,
  iconAltText,
  onInvoke,
  buttonDesign,
  getTooltip,
  getDisabled,
  getHidden,
}: MakeSimpleGroupActionFunctionParams<TGroupMetadata, TCreateGroupExtras>): ToolbarButtonGroupActionFunction<
  TGroupMetadata,
  TCreateGroupExtras
> &
  OverflowMenuGroupActionFunction<TGroupMetadata, TCreateGroupExtras> {
  const renderToolbarButton = (
    actionFunctionParams: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>
  ): JSX.Element => {
    const tooltipText = getTooltip?.(actionFunctionParams); // null means disabled, undefined means use default.
    const evaluatedLabel = evaluateFunctionalParam(label, actionFunctionParams);
    return (
      // eslint-disable-next-line benchlint/no-eqeqeq-null
      <Tooltip title={tooltipText ?? evaluatedLabel} disabled={tooltipText === null}>
        <span>
          <Button
            buttonStyle={evaluateFunctionalParam(
              buttonDesign?.buttonStyle ?? ButtonStyle.TERTIARY_GRAY,
              actionFunctionParams
            )}
            className="u-mini-spacing-left"
            disabled={getDisabled?.(actionFunctionParams)}
            icon={evaluateFunctionalParam(icon, actionFunctionParams)}
            iconAltText={iconAltText ?? tooltipText ?? evaluatedLabel}
            text={buttonDesign?.showTextOnButton ? evaluatedLabel : null}
            onClick={() => {
              onInvoke(actionFunctionParams);
            }}
            size={ButtonSize.MINI}
          />
        </span>
      </Tooltip>
    );
  };
  const renderOverflowMenuItem = (
    actionFunctionParams: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>
  ): OverflowMenuOption => {
    const evaluatedIcon = evaluateFunctionalParam(icon, actionFunctionParams);
    // Does not support JSX icons
    const iconString = typeof evaluatedIcon === 'string' ? evaluatedIcon : undefined;
    return {
      icon: iconString,
      contents: <span>{label as ReactNode}</span>,
      onSelect: () => {
        onInvoke(actionFunctionParams);
      },
      isDisabled: getDisabled?.(actionFunctionParams),
      tooltipTitle: getTooltip?.(actionFunctionParams) ?? undefined,
    };
  };

  return makeCustomGroupButtonActionFunction({
    getHidden,
    renderToolbarButton,
    renderOverflowMenuItem,
  });
}
